import aiSuggestionsAPI from '../../api/aiSuggestions';

export const types = {
  SET_SUGGESTION: 'SET_AI_SUGGESTION',
  CLEAR_SUGGESTION: 'CLEAR_AI_SUGGESTION',
  SET_UI_FLAG: 'SET_AI_SUGGESTION_UI_FLAG',
};

const state = {
  // conversationId -> current pending suggestion (or null)
  byConversation: {},
  uiFlags: {
    fetching: false,
    updating: false,
  },
};

const PENDING_STATUSES = ['pending'];

export const getters = {
  getPendingSuggestion: _state => conversationId => _state.byConversation[conversationId] || null,
  getUIFlags: _state => _state.uiFlags,
};

export const actions = {
  async fetch({ commit }, conversationId) {
    if (!conversationId) return null;
    commit(types.SET_UI_FLAG, { fetching: true });
    try {
      const { data } = await aiSuggestionsAPI.index(conversationId);
      const items = data.data || [];
      const pending = items.find(item => PENDING_STATUSES.includes(item.status));
      commit(types.SET_SUGGESTION, { conversationId, suggestion: pending || null });
      return pending || null;
    } finally {
      commit(types.SET_UI_FLAG, { fetching: false });
    }
  },

  receive({ commit }, suggestion) {
    if (!suggestion?.conversation_id) return;
    if (PENDING_STATUSES.includes(suggestion.status)) {
      commit(types.SET_SUGGESTION, { conversationId: suggestion.conversation_id, suggestion });
    } else {
      commit(types.CLEAR_SUGGESTION, suggestion.conversation_id);
    }
  },

  async accept({ commit, dispatch }, { conversationId, id, send = false }) {
    commit(types.SET_UI_FLAG, { updating: true });
    try {
      const { data } = await aiSuggestionsAPI.accept(conversationId, id);
      commit(types.CLEAR_SUGGESTION, conversationId);
      if (send && data?.final_content) {
        await dispatch(
          'createPendingMessageAndSend',
          {
            conversationId,
            message: data.final_content,
            private: false,
          },
          { root: true }
        );
      }
      return data;
    } finally {
      commit(types.SET_UI_FLAG, { updating: false });
    }
  },

  async reject({ commit }, { conversationId, id }) {
    commit(types.SET_UI_FLAG, { updating: true });
    try {
      const { data } = await aiSuggestionsAPI.reject(conversationId, id);
      commit(types.CLEAR_SUGGESTION, conversationId);
      return data;
    } finally {
      commit(types.SET_UI_FLAG, { updating: false });
    }
  },

  async dismiss({ commit }, { conversationId, id }) {
    commit(types.SET_UI_FLAG, { updating: true });
    try {
      const { data } = await aiSuggestionsAPI.dismiss(conversationId, id);
      commit(types.CLEAR_SUGGESTION, conversationId);
      return data;
    } finally {
      commit(types.SET_UI_FLAG, { updating: false });
    }
  },

  async markEdited({ commit }, { conversationId, id, finalContent }) {
    commit(types.SET_UI_FLAG, { updating: true });
    try {
      const { data } = await aiSuggestionsAPI.update(conversationId, id, {
        status: 'edited',
        final_content: finalContent,
      });
      commit(types.CLEAR_SUGGESTION, conversationId);
      return data;
    } finally {
      commit(types.SET_UI_FLAG, { updating: false });
    }
  },
};

export const mutations = {
  [types.SET_SUGGESTION](_state, { conversationId, suggestion }) {
    _state.byConversation = { ..._state.byConversation, [conversationId]: suggestion };
  },
  [types.CLEAR_SUGGESTION](_state, conversationId) {
    const next = { ..._state.byConversation };
    delete next[conversationId];
    _state.byConversation = next;
  },
  [types.SET_UI_FLAG](_state, data) {
    _state.uiFlags = { ..._state.uiFlags, ...data };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
